import api from 'api';
import { recurrenceBudgetApi } from 'api/recurrenceBudget/recurrenceBudget.api';
import config from 'config';
import { BUDGET_INTERVAL, BUDGET_STATUS, BUDGET_TYPES, RATE_TYPES } from 'constants/project';
import { HAS_CREATED_PROJECT } from 'constants/user';
import { errorMessageFactory } from 'factories/api';
import { userToProjectUserRelationFactory } from 'factories/projects';
import { maxBy, uniq } from 'lodash-es';
import { matchPath } from 'react-router-dom';
import { activateClientId, fetchClient, fetchClients } from 'redux/modules/clients.actions';

// Lazy import to break circular dependency: hours.actions ↔ projects.actions
const getSetHourDraftProject = () =>
  import('redux/modules/hours.actions').then(m => m.setHourDraftProject);

import {
  showAccountExpiredMessage,
  showSubscriptionNoticeModal,
} from 'redux/modules/subscriptions.actions';
import { saveTag } from 'redux/modules/tags.actions';
import { getTagEntities } from 'redux/modules/tags.selectors';
import { showToast } from 'redux/modules/toasts.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import { createXhrStatusModule, xhrStatusQueryInfo } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getAllProjectsLoggingEnabled,
  getCurrentAccount,
  getIsSubscriptionExpiredOrTrialEnded,
  getRecurringBudgetsEnabled,
} from 'redux/selectors/accounts';
import { getClientEntities } from 'redux/selectors/clients';
import { getHourDraft } from 'redux/selectors/hours.draft';
import {
  getEnrichedProjectDraftUsers,
  getHasChangedDefaultTags,
  getHasModifiedProjectRate,
  getProjectBillableChange,
} from 'redux/selectors/project.editor';
import {
  getAllProjectsHaveBeenFetched,
  getDraft,
  getHasReachedProjectLimit,
  getInitialProjectsHaveBeenFetched,
  getPreferredProjectsView,
  getProjectFilter,
  getProjectPermissionsMap,
  getProjectTags,
  getProjectVariantMap,
  getXhrStatus,
} from 'redux/selectors/projects';
import {
  getIsProjectActivitiesRoute,
  getRoutePathName,
  getRouteSearchAsObject,
} from 'redux/selectors/routing';
import { getUserIdsFromTeams } from 'redux/selectors/teams';
import { getUser } from 'redux/selectors/user';
import { getNonDeletedUsersWithSearchResults, getUserEntities } from 'redux/selectors/users';
import { extractProjectBase } from 'redux/selectors/utils/projects';

// Lazy import to break circular dependency: projects.actions ↔ forecast.actions
const getFetchForecasts = () =>
  import('routes/forecast/redux/modules/forecast.actions').then(m => m.fetchForecasts);

import type { IState } from 'redux/state';
import { TrackingActions } from 'tracking/actions';
import type {
  BudgetRecurrence,
  Draft,
  FilterGroupValues,
  LightProject,
  Project,
  ProjectBase,
  ProjectCreateUpdatePayload,
  ProjectUserRelation,
  RateType,
} from 'types/project';
import { ProjectRateChange, TagSettings } from 'types/project';
import type { Thunk } from 'types/redux';
import type { LightUser } from 'types/user';
import { fetchInBatchesOf, fetchPaginated } from 'utils/api';
import debug from 'utils/debug';
import { calculateExchangeRate, generateProjectDraft, mapDefaultTagsForDraft } from 'utils/project';
import { notNull } from 'utils/typesafe';
import { encodeUrlParams } from 'utils/url-params';
import { fetchProjectsActivities } from './activities.actions';
import type {
  FilterBudgetStatus,
  FilterProjectRelation,
  FilterProjectSortBy,
  FilterProjectSortByFields,
  FilterProjectStatus,
} from './projects.types';
import * as types from './projects.types';
import type { TagDraft } from './tags.types';
import { fetchTeams } from './teams';

// Lazy import to break circular dependency: projects.actions ↔ tic.demo.actions
const getEnsureProjectsForDemoMode = () =>
  import('./tic/tic.demo.actions').then(m => m.ensureProjectsForDemoMode);

// Lazy imports to break circular dependency with AutoSheet (timesheet_proposals)
// and the TIC API slice. Used by saveProject to auto-assign a newly-created project
// when the create was initiated from an AutoSheet dropdown.
const getAutoSheetProjectHelpers = () =>
  import('domains/ai/timesheet_proposals/redux/tic.timesheetProposals.slice').then(m => ({
    getPendingProjectCreation: m.getPendingProjectCreation,
    setPendingProjectCreation: m.setPendingProjectCreation,
    setReopenProjectDropdownForEntryId: m.setReopenProjectDropdownForEntryId,
    setTimelineEditorCreatedProjectId: m.setTimelineEditorCreatedProjectId,
    clearEntrySelection: m.clearEntrySelection,
  }));

const getTimelyApi = () =>
  import('redux/modules/timely/api-slice/timely.apiSlice').then(m => m.timelyApi);

const getClaimRangesForProject = () =>
  import('domains/ai/timesheet_proposals/redux/tic.timesheetProposals.actions').then(
    m => m.claimRangesForProject
  );

const { wrapFetching, wrapSaving, abortFetching } = createXhrStatusModule('projects');

export function updateProjectDraftUserId(userId: number, changes: Partial<ProjectUserRelation>) {
  return types.updateProjectUserAssociation({ changes, userId });
}

export function updateProjectDraft(payload: Omit<Partial<Draft>, 'rate_type'> | null) {
  return types.updateDraft(payload);
}

export function setDraftHourRateType(rateType: RateType): Thunk {
  return (dispatch, getState) => {
    const draft = getDraft(getState());
    if (!draft) {
      return;
    }
    const userRelations = draft.users;
    if (rateType === RATE_TYPES.USER_RATE) {
      const usersMap = getUserEntities(getState());
      const relationsWithDefaultRatesApplied = userRelations.map(
        (projectUserRelation): ProjectUserRelation => ({
          ...projectUserRelation,
          hour_rate: usersMap[projectUserRelation.user_id]?.default_hour_rate ?? 0,
        })
      );
      return dispatch(
        types.updateDraft({
          rate_type: rateType,
          users: relationsWithDefaultRatesApplied,
        })
      );
    }
    // Otherwise (setting to non-billable or project rate)
    // null out hour_rate
    const relationsWithoutRates = userRelations.map(
      (projectUserRelation): ProjectUserRelation => ({
        ...projectUserRelation,
        hour_rate: null,
      })
    );
    return dispatch(
      types.updateDraft({
        rate_type: rateType,
        users: relationsWithoutRates,
      })
    );
  };
}

/// Create a list of project user relations, reusing passed draftUsers as far as possible
function mergeExistingAndDraftUsersByIds<TUser extends LightUser>(
  ids: Array<number>,
  users: Array<TUser>,
  draftUsers: Array<ProjectUserRelation>
): Array<ProjectUserRelation> {
  return ids.map(user_id => {
    const draftUser = draftUsers.find(u => u.user_id === user_id);
    if (draftUser != null) {
      return draftUser;
    }
    const userObject = users.find(user => user.id === user_id);
    if (userObject != null) {
      return userToProjectUserRelationFactory(userObject);
    }
    return { user_id, hour_rate: 0 };
  });
}

export const addTeamUserIdsToProjectDraft =
  (type: 'teams', addedTeamIds: Array<number>): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    if (!draft) {
      return;
    }
    const draftUsers = draft.users;
    const allUsers = getNonDeletedUsersWithSearchResults(state);
    const userIds = getUserIdsFromTeams(state, addedTeamIds);
    const draftUserIds = draft.users.map(user => user.user_id);
    const mergeTeamAndDraftIds = uniq([...userIds, ...draftUserIds]);
    const users = mergeExistingAndDraftUsersByIds(mergeTeamAndDraftIds, allUsers, draftUsers);
    dispatch(updateProjectDraft({ users }));
  };

export const removeTeamUserIdsFromProjectDraft =
  (removedTeamIds: Array<number>): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const draft = getDraft(state);
    if (!draft) {
      return;
    }
    const removedTeamUserIds = getUserIdsFromTeams(state, removedTeamIds);
    const remainingTeamUserIds = getUserIdsFromTeams(
      state,
      draft.team_ids.filter(id => !removedTeamIds.includes(id))
    );
    const usersForRemoval = removedTeamUserIds.filter(id => !remainingTeamUserIds.includes(id));
    dispatch(
      updateProjectDraft({
        users: draft.users.filter(u => !usersForRemoval.includes(u.user_id)),
      })
    );
  };

export const addUserIdsToProjectDraft =
  (type: 'users', ids: Array<number>): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const allUsers = getNonDeletedUsersWithSearchResults(state);
    const allDraftUsers = getEnrichedProjectDraftUsers(state);
    // Ensure that the currently logged in user is part of the project if all users are cleared
    if (!ids.length) {
      const user = getUser(state);
      dispatch(
        updateProjectDraft({
          users: [userToProjectUserRelationFactory(user)],
        })
      );
      return;
    }
    const users = mergeExistingAndDraftUsersByIds(ids, allUsers, allDraftUsers);
    dispatch(updateProjectDraft({ users }));
  };

export function removeTagList(id: number, default_ = false): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    dispatch(
      types.tagListRemove({
        id,
        children: getTagEntities(state)[id].children,
      })
    );

    if (default_) {
      dispatch(
        types.tagListChangeDefault({
          tagListId: id,
        })
      );
    }

    const draft = getDraft(state);
    // Are we removing a tag list from project while set to "All"?
    // if so, set the state to "Custom", as that will no longer be true
    if (draft?.enable_labels === TagSettings.All) {
      dispatch(updateProjectDraft({ enable_labels: TagSettings.Custom }));
    }
  };
}

export function changeTagLists(id: number, ids: Array<number>, default_ = false): Thunk {
  return (dispatch, getState) => {
    if (!ids.includes(id)) {
      dispatch(removeTagList(id));

      if (default_) {
        dispatch(
          types.tagListChangeDefault({
            tagListId: id,
          })
        );
      }

      return;
    }

    const initialState = getState();
    const tagEntities = getTagEntities(initialState);
    const tagEntity = tagEntities[id];
    const tagChildren = tagEntity?.children.length > 0 ? tagEntity.children : [];

    // If the tag list already exists as an option, clear it first
    dispatch(types.tagListRemove({ id, children: tagChildren ?? [] }));
    dispatch(types.tagListAdd({ id, default: default_ }));

    // if (!tagChildren) return;

    const children = tagChildren.filter(c => tagEntities[c]?.active === true);
    dispatch(types.tagAddBatch({ ids: children, default: default_ }));
    if (default_) {
      dispatch(
        types.tagListChangeDefault({
          tagListId: id,
          tagId: children[0],
        })
      );
    }
  };
}

export function changeTag(id: number, ids: Array<number>): Thunk {
  return dispatch => {
    if (ids.includes(id)) {
      dispatch(types.tagRemove({ id }));
    } else {
      dispatch(types.tagAdd({ id }));
    }
  };
}

export function toggleRequired(id: number, required: boolean) {
  return types.tagListChangeRequired({
    id,
    required,
  });
}

export function createNewTagList(tagList: TagDraft): Thunk<Promise<unknown>> {
  return dispatch => {
    return dispatch(saveTag(tagList)).then(tag => {
      dispatch(types.tagListAdd({ id: tag.id }));
    });
  };
}

export function changeTagBudget(id: number, budget: number): Thunk {
  return dispatch => dispatch(types.tagChangeBudget({ id, budget }));
}

function fetchRecentProjects(
  userId: number,
  filter: 'active' | 'all' | 'archived',
  count: number
): Thunk<Promise<Array<LightProject>>> {
  return dispatch => {
    const perPage = config.projects.projectsPerPage;
    const pages = Math.ceil(count / perPage);

    return dispatch(
      wrapFetching(
        fetchPaginated({ perPage, pages }, page =>
          api.getRecentProjects(userId, filter, { page, perPage })
        )
      )
    ).then(response => {
      dispatch(types.fetchedLightProjects({ projects: response }));
      return response;
    });
  };
}

/**
 * Tracks whether fetchAllWorkspaceProjects has already run successfully in
 * this session. Distinct from Redux's `fetchedAllLight` status because the
 * existing app-boot loader uses that flag after loading only the user's
 * assigned projects — we need a separate signal for "every workspace
 * project is now in Redux, including those the user isn't a member of".
 *
 * Module-level rather than Redux because it does not need to survive a
 * full page reload (a refresh resets state.projects too) and no other
 * code needs to read it.
 */
let workspaceProjectsFetched = false;

export function workspaceProjectsHaveBeenFetched() {
  return workspaceProjectsFetched;
}

/**
 * Loads every active workspace project into Redux state. Used by the hour
 * editor's project picker when the ALL_PROJECTS_LOGGING feature is enabled,
 * so users can pick (and log time to) projects they are not currently a
 * member of. The backend auto-adds them as members on save.
 *
 * Performance gate: workspaces with more active projects than
 * config.projects.loadLightLimit (4000) are not pre-loaded — they fall
 * back to the typed-search flow (which also passes scope=global) so the
 * dropdown stays responsive. This mirrors the existing
 * fetchAllProjectsForWhenTheWorkspaceIsSmallEnough convention.
 *
 * Idempotent within a session: callers can dispatch on every hour-editor
 * mount; subsequent calls short-circuit until the page is reloaded.
 * Aborts are swallowed silently — `wrapFetching` already provides an
 * AbortSignal that fires when a newer fetch starts.
 */
export function fetchAllWorkspaceProjects(): Thunk<Promise<Array<LightProject>>> {
  return async (dispatch, getState) => {
    if (workspaceProjectsFetched) {
      return [];
    }
    const account = getCurrentAccount(getState());
    if ((account.active_projects_count ?? 0) > config.projects.loadLightLimit) {
      // Workspace is too large to pre-load. Mark fetched so we don't keep
      // trying on every editor mount, and let typed search handle discovery.
      workspaceProjectsFetched = true;
      return [];
    }
    const perPage = config.projects.projectsPerPage;
    try {
      const projects = await dispatch(
        wrapFetching(abortSignal =>
          fetchPaginated(
            { perPage },
            async page =>
              (
                await api.searchForProjects({
                  userId: null,
                  filter: 'active',
                  scope: 'global',
                  per_page: perPage,
                  page,
                  abortSignal,
                })
              ).projects
          )
        )
      );
      if (projects && projects.length > 0) {
        dispatch(types.fetchedLightProjects({ projects }));
        // The picker's dropdown groups projects by client, and non-admins
        // only have the clients they belong to in Redux. Pull every client
        // via scope=global so projects from "other" clients don't get
        // dropped at render time. Failures here are not fatal — the
        // projects still load.
        dispatch(fetchClients({ scope: 'global' })).catch(() => {});
      }
      workspaceProjectsFetched = true;
      return projects ?? [];
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') {
        return [];
      }
      throw e;
    }
  };
}

/**
 * Tracks the `<userId>:<clientId>` pairs already loaded by
 * `fetchProjectsForClient` in this session, so re-selecting the same linked
 * work item does not re-request the same page set. Module-level for the same
 * reason as `workspaceProjectsFetched`: a reload resets state.projects too.
 */
const clientProjectsFetched = new Set<string>();

/**
 * Own abort key for the client-focus fetch. `wrapFetching` aborts whatever is
 * in flight under the same key, so sharing the default one would let opening
 * the hour editor cancel an in-progress `fetchAllWorkspaceProjects` (and be
 * cancelled by typed search in turn). The picker's spinner is unaffected:
 * `getProjectXhrStatus` reads the status without a key, which covers every
 * in-flight fetch regardless of key.
 */
const CLIENT_PROJECTS_KEY = 'CLIENT_PROJECTS_KEY';

/**
 * True when every active project the user belongs to under this client is
 * already loaded *and* registered as a permission — in that case the picker
 * can render the focused list without a round trip. `active_projects_count` is
 * the client's total, so a user who belongs to only some of them never
 * short-circuits; that errs towards fetching, which is the safe direction.
 */
const hasCompleteClientProjects = (state: IState, clientId: number, userId: number) => {
  const total = getClientEntities(state)[clientId]?.active_projects_count;
  if (total == null) {
    return false;
  }
  const variantMap = getProjectVariantMap(state);
  const loaded = (getProjectPermissionsMap(state)[userId] ?? []).filter(projectId => {
    const variant = variantMap[projectId];
    if (!variant) {
      return false;
    }
    const project = extractProjectBase(variant);
    return project.active && project.client?.id === clientId;
  }).length;

  return loaded >= total;
};

/**
 * Loads every *active* project of a single client that the given user is a
 * member of, and registers the resulting memberships.
 *
 * Needed by the hour editor's client focus (`client_focus_on_projects`): the
 * picker only lists projects that are both loaded and present in the user's
 * inferred permission list, and that list is built from the 250 most recently
 * used projects at boot (`fetchInitialProjects`) plus whatever the user has
 * searched for since. A client imported from a Jira custom field is very
 * often outside that slice, so focusing it narrowed the dropdown down to
 * nothing. Fetching by `client_id` closes the gap without depending on
 * workspace size (`fetchAllWorkspaceProjects` skips workspaces over
 * `config.projects.loadLightLimit`).
 *
 * Scoped by `userId`, so the backend still only returns projects that user
 * belongs to — focus never surfaces projects the draft owner cannot log to.
 * Aborts are swallowed and leave the pair unmarked, so a fetch cut short by a
 * newer request is retried the next time the client is focused.
 */
export function fetchProjectsForClient(
  clientId: number,
  userId: number
): Thunk<Promise<Array<LightProject>>> {
  return async (dispatch, getState) => {
    const cacheKey = `${userId}:${clientId}`;
    if (clientProjectsFetched.has(cacheKey)) {
      return [];
    }
    if (hasCompleteClientProjects(getState(), clientId, userId)) {
      clientProjectsFetched.add(cacheKey);
      return [];
    }
    const perPage = config.projects.projectsPerPage;
    try {
      const projects = await dispatch(
        wrapFetching(
          abortSignal =>
            fetchPaginated(
              { perPage },
              async page =>
                (
                  await api.searchForProjects({
                    userId,
                    clientId,
                    filter: 'active',
                    per_page: perPage,
                    page,
                    abortSignal,
                  })
                ).projects
            ),
          CLIENT_PROJECTS_KEY
        )
      );
      if (projects && projects.length > 0) {
        dispatch(types.fetchedLightProjects({ projects }));
        dispatch(insertInferredProjectPermissions(userId, projects));
      }
      clientProjectsFetched.add(cacheKey);
      return projects ?? [];
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') {
        return [];
      }
      throw e;
    }
  };
}

export function searchProjects(
  filterString: string,
  userId: number | null,
  filter: FilterGroupValues = 'active',
  scope?: 'global'
): Thunk<Promise<unknown>> {
  return async dispatch => {
    const query = filterString.trim();
    if (query.length < 2) {
      abortFetching();
      return;
    }
    const perPage = config.projects.projectsPerPage;
    const response = await dispatch(
      wrapFetching(abortSignal =>
        fetchPaginated(
          { perPage },
          async page =>
            (
              await api.searchForProjects({
                userId,
                query,
                filter,
                scope,
                per_page: perPage,
                page,
                abortSignal,
              })
            ).projects
        ).catch(e => {
          if (e.name === 'AbortError') {
            return;
          }
          throw e;
        })
      )
    );

    if (!response) {
      return;
    }

    dispatch(types.fetchedLightProjects({ projects: response }));
    if (userId != null) {
      dispatch(insertInferredProjectPermissions(userId, response));
    }
  };
}

export function fetchInitialProjects(userId: number): Thunk {
  return async (dispatch, getState) => {
    if (!getInitialProjectsHaveBeenFetched(getState())) {
      await dispatch(fetchAllProjectsForWhenTheWorkspaceIsSmallEnough());
    }

    const projects = await dispatch(
      fetchRecentProjects(userId, 'active', config.projects.numRecentProjectsToFetch)
    );
    if (userId && projects.length > 0) {
      dispatch(insertInferredProjectPermissions(userId, projects));
    }
    await dispatch(
      fetchRecentProjects(userId, 'archived', config.projects.numArchivedProjectsToFetch)
    );
    if (!getInitialProjectsHaveBeenFetched(getState())) {
      dispatch(types.setProjectFetchingStatus('fetchedInitial'));
    }
    // With ALL_PROJECTS_LOGGING on, kick off the workspace-wide fetch at
    // boot so the hour-editor picker has every active project ready on
    // first open. Fire-and-forget — failures don't block boot, and the
    // action is idempotent so the editor mount path is a no-op once this
    // resolves.
    if (getAllProjectsLoggingEnabled(getState())) {
      dispatch(fetchAllWorkspaceProjects()).catch(() => {});
    }
    getEnsureProjectsForDemoMode().then(ensureProjectsForDemoMode =>
      dispatch(ensureProjectsForDemoMode())
    );
  };
}

export function insertInferredProjectPermissions(
  userId: number,
  projects: Array<LightProject>
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const existingPermissionsForUser = getProjectPermissionsMap(state)[userId] ?? [];
    const merged = uniq(
      existingPermissionsForUser.concat(
        projects.filter(p => (p.has_permission != null ? p.has_permission : true)).map(p => p.id)
      )
    );

    dispatch(types.setProjectPermissions({ userId, permissions: merged }));
  };
}

/**
 * Fetches projectIds in batches.
 * It's smart enough to avoid re-fetching identical batches, but overlap can occur.
 */
export function fetchFullProjectsIfNeeded(
  projectIds: Array<number> = [],
  options?: { includeTotals?: boolean }
): Thunk<Promise<Array<Project>>> {
  return async (dispatch, getState) => {
    const state = getState();
    const projectVariantMap = getProjectVariantMap(state);

    // Remove any projectIds we already have the full payload of:
    const finalProjectIds = projectIds.filter(id => !(projectVariantMap[id]?.type === 'full'));

    // Abort immediatly if this account loads full project payloads already or no projects to fetch
    if (!finalProjectIds.length) {
      return [];
    }

    const projects = await dispatch(
      wrapFetching(
        fetchInBatchesOf(50, finalProjectIds, batch => api.getProjectIds(batch, options))
      )
    );
    dispatch(types.fetchedFullProjects(projects));
    return projects;
  };
}

export function fetchFullProjectsWithUsersIfNeeded(
  projectIds: Array<number> = []
): Thunk<Promise<Array<Project>>> {
  return async (dispatch, getState) => {
    // Need to fetch projects with api.getProject to get users
    const state = getState();
    const projectVariantMap = getProjectVariantMap(state);

    const cachedProjects: Array<Project> = [];
    const finalProjectIds: Array<number> = [];
    for (const id of projectIds) {
      const variant = projectVariantMap[id];
      if (variant?.type === 'full' && variant.project.users != null) {
        cachedProjects.push(variant.project);
      } else {
        finalProjectIds.push(id);
      }
    }

    if (!finalProjectIds.length) {
      return cachedProjects;
    }

    const fetchProjects = async () => {
      const projects = [];
      for (const projectId of finalProjectIds) {
        const project = await api.getProject(projectId);
        if (project) {
          projects.push(project);
        }
      }
      dispatch(types.fetchedFullProjects(projects));
      return [...cachedProjects, ...projects];
    };

    return dispatch(wrapFetching(fetchProjects()));
  };
}

export function fetchProject(projectId: number): Thunk<Promise<Project>> {
  return async dispatch => {
    const fetchKey = `project-${projectId}`;

    try {
      const project = await dispatch(wrapFetching(api.getProject(projectId), fetchKey));
      dispatch(types.fetchedFullProjects([project]));
      return project;
    } catch (e) {
      dispatch(checkRouteAndRemoveProject(projectId));
      throw e;
    }
  };
}

export const fetchProjectId = fetchProject;

export function fetchProjectAfterPushNotification(projectId: number): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const project = await dispatch(fetchProject(projectId));
    if (getIsProjectActivitiesRoute(state)) {
      dispatch(fetchProjectsActivities());
    }
    // Refresh client project counts
    dispatch(fetchClient(project.client.id));
    // It's possible we could have tasks for this project
    getFetchForecasts().then(fetchForecasts =>
      dispatch(fetchForecasts({ project_ids: [projectId] }))
    );
  };
}

export function checkRouteAndRemoveProject(projectId: number): Thunk {
  return (dispatch, getState, history) => {
    const state = getState();
    const route = getRoutePathName(state);
    // did we select "no team assignment"?
    if (route.includes(`projects/${projectId}`)) {
      history.replace(`/projects`);
    }

    {
      const projectVariantMap = getProjectVariantMap(state);
      const project = projectVariantMap[projectId];
      if (project) {
        // Refresh client project counts
        dispatch(fetchClient(extractProjectBase(project).client.id));
      }
    }

    dispatch(removeProject(projectId));
    dispatch(types.clearDraft());
    dispatch(types.replaceProjectPermissionForAll({ projectId }));
  };
}

/**
 * Fetch the projectId _if_
 * - We don't already have the full project
 * - No project found locally
 * - It's last fetch was over 30 minutes ago
 */
export function fetchProjectIdSmartly(
  projectId: number,
  { needUsers, ignoreAccessDenied }: { needUsers?: boolean; ignoreAccessDenied?: boolean } = {}
): Thunk<Promise<Project | undefined>> {
  return async (dispatch, getState) => {
    const fetchKey = `project-${projectId}`;
    const state = getState();
    const variant = getProjectVariantMap(state)[projectId];
    const rawXHrStatus = getXhrStatus(state);
    const { shouldFetch, isInFlight } = xhrStatusQueryInfo(rawXHrStatus, {
      key: fetchKey,
      maxMinuteAge: 30,
    });

    const hasFullProject =
      variant?.type === 'full' && needUsers === (variant.project.users != null);

    if (hasFullProject) {
      return variant.project;
    }

    if (isInFlight || !shouldFetch) return;

    const project = await dispatch(fetchProjectId(projectId)).catch(error => {
      if (!(ignoreAccessDenied && error?.response?.errors?.message === 'Access denied')) {
        dispatch(showToast({ type: 'danger', message: errorMessageFactory(error) }));
      }
      return undefined;
    });

    if (project?.users?.length) {
      dispatch(
        types.replaceProjectPermissionForAll({
          projectId,
          userIds: project.users.map(u => u.user_id),
        })
      );
    }

    return project;
  };
}

export function createProjectDraft(seed: Partial<Project> = {}): Thunk {
  return (dispatch, getState) => {
    const state = getState();

    // Keep track of how many times the user starts creation process:
    TrackingActions.initiateUserAction('New Project');

    // If subscription is expired, disallow creating new project
    if (getIsSubscriptionExpiredOrTrialEnded(state)) {
      dispatch(showAccountExpiredMessage());
      return;
    } else if (getHasReachedProjectLimit(state)) {
      dispatch(showSubscriptionNoticeModal('projectLimitReached'));
      return;
    }

    // Uh oh! Selector usage here == circular dependency
    // Get the most recently created client (highest ID) as the default
    const activeClients = state.clients.entries.filter(c => c.active);
    const client = seed.client ??
      maxBy(activeClients, 'id') ?? {
        id: null,
        name: undefined,
        color: '',
      };

    const currency = getAccountCurrency(state);

    const recurringBudgetsAvailable = getRecurringBudgetsEnabled(state);

    const generatedPayload = generateProjectDraft(
      { currency, ...seed, client },
      { recurringBudgetsAvailable }
    );

    // If we're creating a new project or otherwise have no users, add the current user by default
    if (!seed.users) {
      const user = userToProjectUserRelationFactory(getUser(state));
      const users = [user];
      generatedPayload.users = users;
    }

    mapDefaultTagsForDraft(generatedPayload, getTagEntities(state));

    dispatch(types.createDraft(generatedPayload));
  };
}

function setDraftOriginal(payload: Project) {
  return types.setDraftOriginal(payload);
}

export function editProject(project: Project): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const baseData: Project = { ...project };

    if (project.client) {
      baseData.client = project.client;
    }

    dispatch(setDraftOriginal(baseData));
    const draft = generateProjectDraft(baseData);

    const tags = getTagEntities(getState());
    mapDefaultTagsForDraft(draft, tags);

    dispatch(types.createDraft(draft));

    const state = getState();
    const canAccessAllTags = project.enable_labels === TagSettings.All;

    if (canAccessAllTags && !draft.labels.length) {
      // We call this with getState() again to get the new state from the updateProjectDraft above.
      // This is such a dumpster fire right now :(
      baseData.labels = getProjectTags(state);
      // TODO revamp
      dispatch(updateProjectDraft({ labels: getProjectTags(state) }));
    }

    // Always fetch updated user associations on the project:

    const updatedProject = await dispatch(fetchProject(baseData.id));

    // If the response is empty or the draft has been closed, don't try to update
    if (!updatedProject || !getDraft(getState())) return;

    const { users, budget_recurrence, billed_cost, locked_hours } = updatedProject;
    const draftData: Partial<Project> = {
      budget_recurrence,
      users,
      billed_cost,
      locked_hours,
    };

    dispatch(setDraftOriginal({ ...baseData, ...draftData }));
    return dispatch(updateProjectDraft(draftData));
  };
}

export function editCopyOfProjectId(projectId: number): Thunk {
  return dispatch => {
    return dispatch(fetchProject(projectId)).then(fetchedProject => {
      if (!fetchedProject) {
        return;
      }
      // Pluck id out of project, otherwise keep identical:
      const { id, ...rest } = fetchedProject;
      const draft = { ...rest, name: `${rest.name} (Copy)` };

      return dispatch(createProjectDraft(draft));
    });
  };
}

export function editProjectId(projectId: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const variant = getProjectVariantMap(state)[projectId];
    if (variant?.type === 'full') {
      dispatch(editProject(variant.project));
    }
  };
}

interface SaveProjectOptions {
  clearDraft?: boolean;
  showToast?: boolean;
  refetchProjectList?: boolean;
  useClientColor?: boolean;
}
export function saveProject(
  payload: ProjectCreateUpdatePayload,
  options: SaveProjectOptions = {}
): Thunk<Promise<Project>> {
  return async (dispatch, getState) => {
    const state = getState();

    // Mark user as having created/saved a project
    dispatch(saveUserProperty(HAS_CREATED_PROJECT, true));

    try {
      const project = await dispatch(wrapSaving(api.saveProject(payload), 'project'));

      dispatch(types.projectSaved({ project, createdEntry: !payload.id }));
      const hourDraft = getHourDraft(state);
      // If we changed the users on the project, replace permissions
      if (payload.users) {
        dispatch(
          types.replaceProjectPermissionForAll({
            projectId: project.id,
            userIds: Object.values(payload.users)
              .map(user => user.user_id)
              .filter(notNull),
          })
        );
      }

      if (options.clearDraft) {
        dispatch(types.clearDraft());
      }
      if (hourDraft != null) {
        const setHourDraftProject = await getSetHourDraftProject();
        dispatch(setHourDraftProject(project.id));
      }

      // AutoSheet: if the create was triggered inline from an AutoSheet dropdown,
      // auto-assign the new project to the originating target (entry / bulk selection).
      // Mirrors the hourDraft branch above. Read from the pre-captured `state` so we
      // don't miss the pending context: options.clearDraft above synchronously fires
      // CLEAR_DRAFT, whose extraReducer resets pendingProjectCreation to null.
      if (!payload.id) {
        const autoSheet = await getAutoSheetProjectHelpers();
        const pending = autoSheet.getPendingProjectCreation(state);
        if (pending) {
          try {
            if (pending.kind === 'row') {
              const timelyApi = await getTimelyApi();
              await dispatch(
                timelyApi.endpoints.patchTimesheetProposalEntry.initiate({
                  entryId: pending.entryId,
                  body: { timely_project_id: project.id, timely_label_ids: [] },
                })
              ).unwrap();
              dispatch(autoSheet.setReopenProjectDropdownForEntryId(pending.entryId));
            } else if (pending.kind === 'batchReassign') {
              const timelyApi = await getTimelyApi();
              const results = await Promise.allSettled(
                pending.entryIds.map(entryId =>
                  dispatch(
                    timelyApi.endpoints.patchTimesheetProposalEntry.initiate({
                      entryId,
                      body: { timely_project_id: project.id, timely_label_ids: [] },
                    })
                  ).unwrap()
                )
              );
              const failCount = results.filter(r => r.status === 'rejected').length;
              if (failCount > 0) {
                dispatch(
                  showToast({
                    message: `Failed to reassign ${failCount} ${
                      failCount === 1 ? 'entry' : 'entries'
                    } to the new project`,
                    type: 'danger',
                  })
                );
              }
              // Matches the normal handleBatchReassignProject path: after the
              // batch patch completes, drop the selection so BatchActionBar hides.
              dispatch(autoSheet.clearEntrySelection());
            } else if (pending.kind === 'claimRanges') {
              const claimRangesForProject = await getClaimRangesForProject();
              await dispatch(
                claimRangesForProject({
                  proposalId: pending.proposalId,
                  ranges: pending.ranges,
                  projectId: project.id,
                  labelIds: [],
                  resourceId: null,
                })
              );
            } else if (pending.kind === 'timelineEditor') {
              // The editor owns the commit: hand it the id and let Save write
              // the whole draft (project, note, tags, linked work, times).
              dispatch(autoSheet.setTimelineEditorCreatedProjectId(project.id));
            }
          } finally {
            dispatch(autoSheet.setPendingProjectCreation(null));
          }
        }
      }

      const pathname = getRoutePathName(state);
      if (options.showToast && !pathname.endsWith(`/projects/${project.id}`)) {
        const notificationState = payload.id ? 'updated' : 'created';
        dispatch(
          showToast({
            message: `${project.name} ${notificationState}`,
            type: 'success',
          })
        );
      }

      if (options.refetchProjectList) {
        dispatch(resetPaginatedProjectView());
      }

      return project;
    } catch (error) {
      const action = payload.id ? 'update' : 'create';

      dispatch(
        showToast({
          message: `Cannot ${action} project. ${errorMessageFactory(error)}`,
          type: 'danger',
        })
      );
      throw error;
    }
  };
}

export function saveProjectDraft(options: SaveProjectOptions = {}): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const draft = getDraft(getState());
    if (!draft) {
      return Promise.resolve();
    }
    const project = await dispatch(saveProjectDraftObject(draft, options));
    await dispatch(fetchClient(project.client.id));
    await dispatch(fetchTeams());
  };
}

export function saveProjectDraftObject(
  draft: Draft,
  options: SaveProjectOptions = {}
): Thunk<Promise<Project>> {
  return async (dispatch, getState) => {
    const requireAtLeastOneTag = draft.required_labels && draft.enable_labels !== TagSettings.None;

    const payload: ProjectCreateUpdatePayload = {
      name: draft.name,
      client_id: draft.client.id!,
      color: options.useClientColor ? null : draft.color,
      billable: draft.billable,
      required_notes: draft.required_notes,
      required_labels: requireAtLeastOneTag,
      enable_labels: draft.enable_labels,
      invoice_by_budget: draft.invoice_by_budget,
      currency_code: draft.currency.id,
      default_labels: !!draft.default_labels,
      team_ids: draft.team_ids.filter(id => id !== 0),
      allow_only_one_tag: draft.allow_only_one_tag,
    };

    if (draft.exchange_rate) {
      payload.exchange_rate = calculateExchangeRate(draft.exchange_rate);
    }

    if (draft.id) {
      payload.id = draft.id;
    }

    if (draft.description !== undefined) {
      payload.description = draft.description?.trim();
    }

    if (draft.active != null) {
      payload.active = draft.active;
    }

    if (draft.external_id != null) {
      payload.external_id = draft.external_id;
    }

    if (draft.budget) {
      payload.budget = Number(draft.budget);
      payload.budget_type = draft.budget_type;
    }

    if (draft.budget_type === BUDGET_TYPES.NONE) {
      payload.budget = 0;
      payload.budget_type = null;
    }

    if (draft.rate_type) {
      payload.rate_type = draft.rate_type;
    }

    // Only set hour_rate on project when it's not defined per-user and is billable:
    if (draft.rate_type !== 'user' && draft.billable) {
      payload.hour_rate = Number(draft.hour_rate);
    }

    if (draft.budget_recurrence && draft.budget_recurrence.recur !== BUDGET_INTERVAL.NONE) {
      payload.budget_recurrence = draft.budget_recurrence;
    }

    if (draft.budget_recurrence && draft.budget_recurrence.recur === BUDGET_INTERVAL.NONE) {
      payload.budget_recurrence = null;
    }

    // Recurring budget for the next period (TT-5786) — scheduled on the backend
    // as part of the project save; the current period is unaffected.
    if (draft.upcoming_budget != null) {
      payload.upcoming_budget = Number(draft.upcoming_budget);
    }

    // Only add [labels] to payload if enable_labels is Custom or All
    if (draft.enable_labels !== TagSettings.None) {
      payload['labels'] = draft.labels.map(label => ({
        // We store the required status in required_label_ids during editing, so we need to
        // add it back to the payloads before sending
        ...label,
        // When "Require at least one tag" OR "Allow only one tag" is checked, none of the tag lists are required
        required:
          !requireAtLeastOneTag &&
          !draft.allow_only_one_tag &&
          draft.required_label_ids?.includes(label.label_id) === true,
      }));
    }

    // We check for draft.users && draft.users.length because not all callees pass a complete project draft to this function [ch6241]
    if (draft.users && draft.users.length) {
      if (draft.rate_type === RATE_TYPES.USER_RATE || draft.rate_type === RATE_TYPES.NON_BILLABLE) {
        // If hour_rate hasn't been specified for the user or is 0, fall back to project's hour rate.
        // This works around bug where you set a global rate -> change to individual rates without
        // actually setting the rate per user (UI shows project's hour_rate in that case).
        payload.users = draft.users.map(user => {
          return {
            user_id: user.user_id,
            hour_rate: user.hour_rate,
          };
        });
        // Don't include users hour_rate when rate is set on project
      } else if (draft.rate_type === RATE_TYPES.PROJECT_RATE) {
        payload.users = draft.users.map(user => {
          return {
            user_id: user.user_id,
          };
        });
      }
    }

    if (draft.default_labels) {
      payload.default_labels = draft.default_labels;
      const defaultTagIds = Object.entries(draft.defaultTags ?? {})
        .flat()
        .map(id => +id);

      payload.labels = (payload.labels ?? draft.labels ?? []).filter(
        label => !label.default || defaultTagIds.includes(label.label_id)
      );
    }

    const state = getState();

    if (getProjectBillableChange(state) != null && draft.update_hour_billable_state != null) {
      payload.update_hour_billable_state = draft.update_hour_billable_state;
    }

    if (getHasModifiedProjectRate(state) && draft.update_hour_rates != null) {
      payload.update_existing_hours = draft.update_hour_rates !== ProjectRateChange.NewOnly;
      payload.update_unbilled_only = draft.update_hour_rates === ProjectRateChange.UnbilledOnly;
    }

    if (getHasChangedDefaultTags(state) && draft.update_hour_default_labels != null) {
      payload.update_hours_default_labels = draft.update_hour_default_labels;
    }

    const project = await dispatch(saveProject(payload, options));

    // The save can change the per-period schedule (upcoming_budget, or a
    // currency change rescaling the stored periods), so drop the cached
    // recurrence-budget history for this project (TT-5786).
    if (project?.id != null) {
      dispatch(
        recurrenceBudgetApi.util.invalidateTags([{ type: 'RecurrenceBudget', id: project.id }])
      );
    }

    return project;
  };
}

export function archiveProject(project: Project): Thunk {
  return async (dispatch, getState) => {
    dispatch(types.archiveProject(project.id));
    const draft = generateProjectDraft({ ...project, active: false });
    const tags = getTagEntities(getState());
    mapDefaultTagsForDraft(draft, tags);
    await dispatch(saveProjectDraftObject(draft));
    await dispatch(fetchClient(project.client.id));
  };
}

export function archiveProjectId(projectId: number): Thunk<Promise<unknown>> {
  return async dispatch => {
    const project = await dispatch(fetchProject(projectId));
    if (project) {
      return dispatch(archiveProject(project));
    }
  };
}

export function activateProject(project: Project): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const draft = generateProjectDraft({ ...project, active: true });
    const tags = getTagEntities(getState());
    mapDefaultTagsForDraft(draft, tags);
    await dispatch(saveProjectDraftObject(draft));
    await dispatch(fetchClient(project.client.id));
  };
}

export function activateProjectId(projectId: number): Thunk<Promise<unknown>> {
  return async dispatch => {
    const project = await dispatch(fetchProject(projectId));
    if (project) {
      const { client } = project;

      if (!client.active) {
        dispatch(activateClientId(client.id));
      }
      return dispatch(activateProject(project));
    }
  };
}

export function removeProject(projectId: number) {
  return types.removeProject(projectId);
}

export function deleteProject(project: ProjectBase): Thunk<Promise<unknown>> {
  return async (dispatch, getState, historyAdapter) => {
    await api.deleteProject(project.id);

    const state = getState();
    if (matchPath(`/:account_id/projects/${project.id}/*`, state.router.location.pathname)) {
      const preferredProjectsView = getPreferredProjectsView(state);
      historyAdapter.replace(`/projects/${preferredProjectsView}`);
    }

    dispatch(removeProject(project.id));
    await dispatch(fetchClient(project.client.id));

    dispatch(
      showToast({
        type: 'success',
        message: `Project ${project.name} deleted`,
      })
    );
  };
}

export function deleteProjectId(projectId: number): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const project = getProjectVariantMap(getState())[projectId];

    debug.assert(
      !!project,
      `Tried to delete project with ID %i but it wasn't found in the Redux store`,
      projectId
    );

    if (project) {
      await dispatch(deleteProject(extractProjectBase(project)));
    }
  };
}

interface SetFilter {
  sortBy?: FilterProjectSortBy;
  projectStatus?: FilterProjectStatus;
  projectRelation?: FilterProjectRelation;
  budgetStatus?: FilterBudgetStatus;
  labelIds?: Array<number>;
  clientIds?: Array<number>;
  query?: string;
}

export function setFilter(payload: SetFilter = {}, updateUrl: boolean = true): Thunk {
  return (dispatch, getState, history) => {
    const state = getState();
    const oldFilter = getProjectFilter(state);
    const filter: types.StateFilter = {
      ...oldFilter,
      ...payload,
      sortBy: { ...oldFilter.sortBy, ...payload.sortBy },
    };

    if (updateUrl) {
      const url = encodeUrlParams({
        projectStatus: filter.projectStatus,
        projectRelation: filter.projectRelation,
        budgetStatus: filter.budgetStatus,
        field: filter.sortBy.field,
        direction: filter.sortBy.direction,
        labelIds: filter.labelIds,
        clientIds: filter.clientIds,
        query: filter.query || undefined,
      });

      history.push(url);
    }

    dispatch(types.setFilter(filter));
    dispatch(resetPaginatedProjectView());
  };
}

const resetPaginatedProjectView = (): Thunk => dispatch => {
  dispatch(types.setPaginatedProjectView(null));
};

export const fetchProjectsPage =
  (pageNumber: number): Thunk =>
  async (dispatch, getState) => {
    const { filter, paginatedView } = getState().projects;
    const totalClientsCount = getState().clients.entries.length;

    const promise = api.getProjectsWithTotalCount(
      {
        state: filter.projectStatus,
        relation: filter.projectRelation,
        sort: filter.sortBy.field,
        order: filter.sortBy.direction,
        budget:
          filter.budgetStatus === BUDGET_STATUS.BILLABLE
            ? true
            : filter.budgetStatus === BUDGET_STATUS.NON_BILLABLE
              ? false
              : undefined,
        labelIds: filter.labelIds.length ? filter.labelIds : undefined,
        clientIds:
          filter.clientIds.length && filter.clientIds.length < totalClientsCount
            ? filter.clientIds
            : undefined,
        page: pageNumber,
        query: filter.query || undefined,
        perPage: config.projects.projectsPerPage,
      },
      {
        includeTotals: true,
      }
    );

    // Update page number while waiting for data
    dispatch(
      types.setPaginatedProjectView({
        page: pageNumber,
        projectIds: paginatedView?.projectIds ?? [],
        totalCount: paginatedView?.totalCount,
      })
    );

    const { projects, totalCount } = await dispatch(wrapFetching(promise, 'projectList'));
    dispatch(types.fetchedFullProjects(projects));
    dispatch(
      types.setPaginatedProjectView({
        page: pageNumber,
        projectIds: projects.map(project => project.id),
        totalCount: totalCount ?? undefined,
      })
    );
  };

const fetchAllProjectsForWhenTheWorkspaceIsSmallEnough =
  (): Thunk<Promise<void>> => async (dispatch, getState) => {
    const state = getState();
    if (getAllProjectsHaveBeenFetched(state)) {
      return;
    }

    const account = getCurrentAccount(state);
    const numActive = account.active_projects_count;
    const numArchived = account.total_projects_count - numActive;

    if (numActive > config.projects.loadLightLimit) {
      // Skip fetching all projects
      return;
    }

    const loadActiveFully = numActive <= config.projects.loadFullyLimit;
    const loadArchivedAtAll = numArchived <= config.projects.loadLightLimit;

    const activeProjectsPromise = dispatch(
      wrapFetching(
        fetchPaginated({ perPage: 1000 }, (page, perPage) =>
          api.getProjects(
            {
              page,
              perPage,
              state: 'active',
            },
            {
              light: !loadActiveFully,
              includeTotals: loadActiveFully,
            }
          )
        )
      )
    );
    const archivedProjectsPromise = loadArchivedAtAll
      ? dispatch(
          wrapFetching(
            fetchPaginated({ perPage: 1000 }, (page, perPage) =>
              api.getProjects(
                {
                  page,
                  perPage,
                  state: 'archived',
                },
                {
                  light: true,
                }
              )
            )
          )
        )
      : Promise.resolve([]);
    const activeProjects = await activeProjectsPromise;
    if (loadActiveFully) {
      // Need some type juggling here, but the API returns different
      // types depending on the light (version) flag
      dispatch(types.fetchedFullProjects(activeProjects));
    } else {
      const lightProjects = activeProjects as unknown as Array<LightProject>;
      dispatch(types.fetchedLightProjects({ projects: lightProjects }));
    }
    const archivedProjects = await archivedProjectsPromise;
    if (archivedProjects.length > 0) {
      const lightProjects = archivedProjects as unknown as Array<LightProject>;
      dispatch(types.fetchedLightProjects({ projects: lightProjects }));
    }

    dispatch(
      types.setProjectFetchingStatus(
        loadActiveFully
          ? loadArchivedAtAll
            ? 'fetchedAllFull'
            : 'fetchedAllActiveFull'
          : loadArchivedAtAll
            ? 'fetchedAllLight'
            : 'fetchedAllActiveLight'
      )
    );
  };

const validSortByFields: ReadonlySet<string> = new Set<FilterProjectSortByFields>([
  'name',
  'client_name',
  'budget',
  'budget_percent',
]);

function isValidSortByField(field: string): field is FilterProjectSortByFields {
  return validSortByFields.has(field);
}

interface UrlFilterParams {
  field: string;
  direction: 'asc' | 'desc' | string;
  projectStatus?: FilterProjectStatus;
  budgetStatus?: FilterBudgetStatus;
  query?: string;
}
export function setFilterFromUrl(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const currentFilter = getProjectFilter(state);
    const urlState: Partial<UrlFilterParams> = getRouteSearchAsObject(state);
    const filter: types.StateFilter = { ...currentFilter, sortBy: { ...currentFilter.sortBy } };

    if (urlState.field && isValidSortByField(urlState.field)) {
      filter.sortBy.field = urlState.field;
    }
    if (urlState.direction) {
      filter.sortBy.direction = urlState.direction === 'desc' ? 'desc' : 'asc';
    }
    if (urlState.projectStatus) {
      filter.projectStatus = urlState.projectStatus;
    }
    if (urlState.budgetStatus) {
      filter.budgetStatus = urlState.budgetStatus;
    }
    if (urlState.query) {
      filter.query = urlState.query;
    }

    dispatch(setFilter(filter, false));
  };
}

export function updateDraftBudgetRecurrence(payload: Partial<BudgetRecurrence>) {
  return types.updateDraftBudgetRecurrence(payload);
}

export function updateBudgetRecurrenceStart(payload: string) {
  return types.updateDraftBudgetRecurrenceStart(payload);
}

export function updateBudgetRecurrenceEnd(payload: string | null) {
  return types.updateDraftBudgetRecurrenceEnd(payload);
}

export function getProjectBudgets(projectId: number): Thunk {
  return dispatch =>
    dispatch(wrapFetching(api.getProjectBudgets(projectId), 'budgets')).then(payload => {
      dispatch(types.fetchedBudgets({ budgets: payload, projectId }));
    });
}
